import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ActionItemButton } from '@components/button/src/action-button';
import { IconCrossedTools, IconSupport } from '@components/icons';
import { ConfirmModal } from '@components/modals';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { USER_SETTINGS_ROUTE } from '@constants/routes';
import { SM_VIEWPORT } from '@constants/viewport';
import { AuthContext } from '@contexts/auth/auth-context';
import { useSignout } from '@hooks/auth/use-signout';
import { useTokenManagement } from '@hooks/auth/use-token-management';
import { useWindowSize } from '@hooks/use-window-size';
import { REFRESH_TOKEN_KEY } from '@utils/token/constants';
import Cookies from 'js-cookie';

import { IMPERSONATE_STEP_LIST, ImpersonateUserSelectContentProps } from './impersonate-user-modal.type';

export const ImpersonateUserSelectContent = ({ setStep, handleClose }: ImpersonateUserSelectContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobileViewPort = useWindowSize(SM_VIEWPORT);
  const { signOut } = useContext(AuthContext);
  const { getIsAdmin } = useTokenManagement();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const { mutate: signout, isSuccess: isSignoutSuccess } = useSignout();

  const handleClickListOption = () => setStep(IMPERSONATE_STEP_LIST);
  const handleClickOpenSettings = () => navigate(USER_SETTINGS_ROUTE);
  const handleClickLogOut = () => {
    const refreshToken = Cookies.get(REFRESH_TOKEN_KEY as string) ?? '';
    signout({ refreshToken });
  };

  useEffect(() => {
    if (isSignoutSuccess) {
      signOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignoutSuccess]);

  return (
    <>
      <ModalStickyHeader
        title={getIsAdmin() ? t('common:impersonateModalTitle') : t('common:impersonateModalTitleUser')}
        handleClose={handleClose}
      />
      <div className='px-4 md:px-10 mb-8 mx-auto' data-testid='impersonate-modal--select-content'>
        <div className='flex flex-col sm:flex-row justify-center gap-2'>
          <ActionItemButton
            active={false}
            onClick={handleClickOpenSettings}
            data-testid='impersonate-modal--settings-button'
            text={t('common:userSettingsLabel')}
            Icon={isMobileViewPort ? undefined : IconCrossedTools}
          />

          {getIsAdmin() && (
            <ActionItemButton
              active={false}
              onClick={handleClickListOption}
              data-testid='impersonate-modal--change-user-button'
              text={t('common:changeUserLabel')}
              Icon={isMobileViewPort ? undefined : IconSupport}
              customTextColor='text-primary'
            />
          )}

          <ActionItemButton
            active={false}
            onClick={() => setIsConfirmModalOpen(true)}
            data-testid='impersonate-modal--log-out-button'
            text={t('common:logout')}
            customTextColor='text-critical'
          />
        </div>
      </div>
      <ConfirmModal
        title={t('common:logoutDescription')}
        isOpen={isConfirmModalOpen}
        handleClose={() => setIsConfirmModalOpen(false)}
        handleClickConfirm={handleClickLogOut}
        handleClickDeny={() => setIsConfirmModalOpen(false)}
      />
    </>
  );
};
